import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Loader2, Ban, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const REASON_LABELS = {
  poor_performance: 'Poor performance',
  not_a_fit: 'Not a fit',
  reliability: 'Reliability concerns',
  conduct: 'Conduct',
  other: 'Other',
};

/**
 * Client-facing management of blocked talent. Blocked talent are hidden from
 * this company's discovery, recommendations and future invitations. Talent are
 * never notified. Blocks are shared across the company's team.
 */
const BlockedTalent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/client/blocked-talent');
      setBlocks(r.data?.blocks || []);
    } catch {
      toast.error('Failed to load blocked talent');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unblock = async (talentId) => {
    setBusy(talentId);
    try {
      await api.delete(`/client/blocked-talent/${talentId}`);
      toast.success('Talent unblocked');
      setBlocks((prev) => prev.filter((b) => b.talent_id !== talentId));
    } catch {
      toast.error('Failed to unblock');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-blocked-talent-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/discover')} data-testid="blocked-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-xl font-bold flex items-center gap-2 text-gray-900">
            <Ban className="w-5 h-5 text-rose-500" /> Blocked Talent
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500 mb-6">
          Blocked talent won't appear in your recommendations, discovery, or future
          opportunities. They are never notified. Unblock anytime to restore them.
        </p>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        ) : blocks.length === 0 ? (
          <Card data-testid="blocked-empty">
            <CardContent className="py-16 text-center text-gray-500">
              <Ban className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-semibold text-gray-800">No blocked talent</p>
              <p className="text-sm mt-1">You can block a talent from their profile.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="blocked-list">
            {blocks.map((b) => (
              <Card key={b.id} data-testid={`blocked-row-${b.talent_id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-rose-50 flex items-center justify-center shrink-0">
                    {b.talent_photo ? <img src={b.talent_photo} alt="" className="w-full h-full object-cover" /> : <Ban className="w-5 h-5 text-rose-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{b.talent_name}</p>
                    <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                      {b.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.location}</span>}
                      <span>· {REASON_LABELS[b.reason_code] || b.reason_code}</span>
                      {b.reason_text && <span className="text-gray-400">· "{b.reason_text}"</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" disabled={busy === b.talent_id}
                    onClick={() => unblock(b.talent_id)} data-testid={`unblock-btn-${b.talent_id}`}>
                    {busy === b.talent_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unblock'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedTalent;
